import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { formatCOP, useCatalog, useSeo } from "@/lib/site";

const ProductEditor = ({ p, onSaved }) => {
  const [f, setF] = useState({ price_cop: p.price_cop, available: p.available, description: p.description });
  const save = async () => {
    try { await api.put(`/admin/products/${p.slug}`, { ...f, price_cop: Number(f.price_cop) }); toast.success(`Tamaño ${p.size} guardado`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`admin-product-${p.slug}`} className="border border-[#080808]/10 bg-white p-6 space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="font-serif-display text-3xl">{p.size} <span className="text-base text-[#080808]/60">{p.width_cm} × {p.height_cm} cm</span></h3>
        <span className="text-sm">{formatCOP(p.price_cop)}</span>
      </div>
      <div className="space-y-2"><Label>Precio (COP, sin decimales)</Label>
        <Input data-testid={`admin-product-${p.slug}-price`} type="number" min={1000} step={100} value={f.price_cop} onChange={(e) => setF({ ...f, price_cop: e.target.value })} /></div>
      <div className="space-y-2"><Label>Descripción</Label>
        <Textarea data-testid={`admin-product-${p.slug}-description`} rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-3 text-sm"><Switch data-testid={`admin-product-${p.slug}-available`} checked={f.available} onCheckedChange={(v) => setF({ ...f, available: v })} /> Disponible</label>
        <Button data-testid={`admin-product-${p.slug}-save`} onClick={save} className="rounded-full px-6">Guardar</Button>
      </div>
    </div>
  );
};

const ContactEditor = ({ contact, onSaved }) => {
  const [f, setF] = useState(contact);
  useEffect(() => setF(contact), [contact]);
  const save = async () => {
    try { await api.put("/admin/contact", f); toast.success("Contacto guardado"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label) => (
    <div className="space-y-2"><Label>{label}</Label><Input data-testid={`admin-contact-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
  );
  return (
    <div data-testid="admin-contact" className="border border-[#080808]/10 bg-white p-6 space-y-4">
      {field("whatsapp", "WhatsApp")}{field("instagram", "Instagram (sin @)")}{field("shipping_origin", "Origen de envíos")}
      <Button data-testid="admin-contact-save" onClick={save} className="rounded-full px-6">Guardar contacto</Button>
    </div>
  );
};

export default function CatalogAdmin() {
  useSeo({ title: "Catálogo · Administración", noindex: true });
  const qc = useQueryClient();
  const { data } = useCatalog();
  const refresh = () => qc.invalidateQueries({ queryKey: ["catalog"] });
  if (!data) return <div className="min-h-screen grid place-items-center eyebrow">Cargando…</div>;
  return (
    <div className="min-h-screen bg-[#FAF8F4] px-5 sm:px-10 py-10">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-4xl">Catálogo y contacto</h1>
        <Link to="/studio" data-testid="admin-back-studio" className="text-xs uppercase tracking-[0.2em] text-[#080808]/70 hover:text-[#080808]">← Estudio</Link>
      </div>
      <p className="mt-3 text-sm text-[#080808]/65">Los cambios se reflejan de inmediato en la tienda.</p>
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {data.products.map((p) => <ProductEditor key={p.slug} p={p} onSaved={refresh} />)}
      </div>
      <h2 className="mt-12 font-serif-display text-2xl">Contacto</h2>
      <div className="mt-4 max-w-xl"><ContactEditor contact={data.contact} onSaved={refresh} /></div>
    </div>
  );
}
